import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CheckCircle2, RefreshCwIcon, XCircle } from "lucide-react";

import {
  useSite,
  useSiteCameras,
  useSiteHealth,
  useTestSiteConnection,
  useUpdateSite,
} from "@/api/sites";
import { useStatusFeed } from "@/api/status_feed";
import { CameraFeedPlayer } from "@/components/CameraFeedPlayer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { formatDate } from "@/lib/utils";
import type { Site, SiteProtocol } from "@/types";


const PROTOCOL_OPTIONS: Array<{
  value: SiteProtocol;
  label: string;
  hint: string;
}> = [
  { value: "onvif", label: "ONVIF (Universal — recommended)", hint: "Works with most vendors. Health data limited on Profile-S devices." },
  { value: "isapi", label: "Hikvision ISAPI", hint: "Richest health data on Hikvision NVRs (HDD SMART, CPU/mem)." },
  { value: "dahua", label: "Dahua HTTP API", hint: "CGI-style HTTP for Dahua cameras and NVRs." },
  { value: "rtsp", label: "RTSP Only (no health data)", hint: "Video stream only. Health methods return unknown." },
];

type Tab = "cameras" | "events" | "settings";

function healthColor(pct: number): string {
  if (pct > 80) return "bg-emerald-500";
  if (pct >= 50) return "bg-amber-500";
  return "bg-red-500";
}

// Sprint 10 fix — NVR config + Test Connection. PATCH /sites/{id} accepts
// nvr_host/port/user/pass; the password is encrypted server-side and never
// echoed back, so the input field shows blank-equals-keep semantics
// (sending empty string clears it; not sending the field leaves it alone).
function NVRConfigCard({ site }: { site: Site }) {
  const [protocol, setProtocol] = useState<SiteProtocol>(site.protocol);
  const [host, setHost] = useState(site.nvr_host ?? "");
  const [port, setPort] = useState(site.nvr_port ?? 80);
  const [user, setUser] = useState(site.nvr_user ?? "");
  const [pass, setPass] = useState("");  // never pre-filled; blank = unchanged
  const [test, setTest] = useState<
    null | { ok: true; msg: string; latency_ms?: number | null } | { ok: false; msg: string; latency_ms?: number | null }
  >(null);
  const update = useUpdateSite(site.id);
  const testConn = useTestSiteConnection(site.id);

  // Re-sync inputs when the site prop changes (e.g. after a save).
  useEffect(() => {
    setProtocol(site.protocol);
    setHost(site.nvr_host ?? "");
    setPort(site.nvr_port ?? 80);
    setUser(site.nvr_user ?? "");
  }, [site.protocol, site.nvr_host, site.nvr_port, site.nvr_user]);

  // RTSP is camera-scoped — host/port/user/pass live on cameras instead.
  const needsCreds = protocol !== "rtsp";

  async function save() {
    setTest(null);
    const patch: {
      protocol?: SiteProtocol;
      nvr_host?: string;
      nvr_port?: number;
      nvr_user?: string;
      nvr_pass?: string;
    } = {
      protocol,
    };
    if (needsCreds) {
      patch.nvr_host = host || undefined;
      patch.nvr_port = port;
      patch.nvr_user = user || undefined;
      if (pass) patch.nvr_pass = pass;
    }
    await update.mutateAsync(patch as never);
    setPass("");
  }

  async function testConnection() {
    setTest(null);
    // Save first so the protocol-aware probe uses the latest config.
    if (needsCreds || protocol !== site.protocol) {
      await save();
    }
    try {
      const result = await testConn.mutateAsync({});
      if (result.ok) {
        setTest({
          ok: true,
          msg: `Connected via ${result.protocol.toUpperCase()}${result.latency_ms != null ? ` (${result.latency_ms} ms)` : ""}.`,
          latency_ms: result.latency_ms ?? null,
        });
      } else {
        setTest({
          ok: false,
          msg: result.message ?? "Connection failed",
          latency_ms: result.latency_ms ?? null,
        });
      }
    } catch (e) {
      const detail =
        (e as { response?: { data?: { detail?: string } } }).response?.data?.detail;
      setTest({
        ok: false,
        msg: typeof detail === "string"
          ? detail
          : (e as { message?: string })?.message ?? "Unknown error",
      });
    }
  }

  const selectedProtocolHint = PROTOCOL_OPTIONS.find((p) => p.value === protocol)?.hint;
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">NVR / Camera Configuration</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <Label htmlFor="site-protocol">Protocol</Label>
          <select
            id="site-protocol"
            value={protocol}
            onChange={(e) => setProtocol(e.target.value as SiteProtocol)}
            className="w-full rounded border border-input bg-background px-2 py-1.5 text-sm"
          >
            {PROTOCOL_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          {selectedProtocolHint && (
            <p className="mt-1 text-xs text-muted-foreground">
              {selectedProtocolHint}
            </p>
          )}
        </div>
        {needsCreds ? (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div>
              <Label htmlFor="nvr-host">IP / Host</Label>
              <Input
                id="nvr-host"
                value={host}
                onChange={(e) => setHost(e.target.value)}
                placeholder="192.168.1.10"
                className="font-mono text-xs"
              />
            </div>
            <div>
              <Label htmlFor="nvr-port">HTTP Port</Label>
              <Input
                id="nvr-port"
                type="number"
                value={port}
                onChange={(e) => setPort(Number(e.target.value) || 80)}
              />
            </div>
            <div>
              <Label htmlFor="nvr-user">Username</Label>
              <Input
                id="nvr-user"
                value={user}
                onChange={(e) => setUser(e.target.value)}
                placeholder="admin"
              />
            </div>
            <div>
              <Label htmlFor="nvr-pass">Password</Label>
              <PasswordInput
                id="nvr-pass"
                value={pass}
                onChange={(e) => setPass(e.target.value)}
                placeholder={site.nvr_configured ? "Leave blank to keep current" : ""}
              />
            </div>
          </div>
        ) : (
          <div className="rounded border border-dashed border-border bg-muted/20 p-3 text-xs text-muted-foreground">
            RTSP-only sites have no NVR-level credentials. Configure each
            camera's RTSP URL on its detail page; "Test Connection" runs
            a stream check against an individual camera via the
            <code className="mx-1 font-mono">?camera_id=…</code> query
            param.
          </div>
        )}

        {/* Identity readback */}
        {site.nvr_configured && (
          <div className="rounded border border-border bg-muted/30 p-3 text-xs">
            <div className="grid grid-cols-2 gap-y-1 md:grid-cols-4">
              <div><span className="text-muted-foreground">Make:</span> {site.nvr_make ?? "—"}</div>
              <div><span className="text-muted-foreground">Model:</span> {site.nvr_model ?? "—"}</div>
              <div><span className="text-muted-foreground">Firmware:</span> {site.nvr_firmware ?? "—"}</div>
              <div><span className="text-muted-foreground">Serial:</span> {site.nvr_serial ?? "—"}</div>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between gap-2">
          <div className="text-xs text-muted-foreground">
            {site.nvr_configured ? (
              <span className="inline-flex items-center gap-1 text-emerald-600">
                <CheckCircle2 className="h-3 w-3" /> Configured
              </span>
            ) : (
              <span>Not yet configured.</span>
            )}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={testConnection}
              disabled={
                (needsCreds && (!host || !user)) ||
                testConn.isPending ||
                update.isPending
              }
            >
              <RefreshCwIcon
                className={cn(
                  "mr-1 h-3.5 w-3.5",
                  (testConn.isPending || update.isPending) && "animate-spin",
                )}
              />
              Test Connection
            </Button>
            <Button
              size="sm"
              onClick={save}
              disabled={update.isPending}
              className="bg-[#ff6d00] text-white hover:bg-[#ff6d00]/90"
            >
              {update.isPending ? "Saving…" : "Save"}
            </Button>
          </div>
        </div>

        {test && (
          <div
            className={cn(
              "flex items-start gap-2 rounded border p-2 text-xs",
              test.ok
                ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                : "border-destructive/40 bg-destructive/5 text-destructive",
            )}
          >
            {test.ok ? <CheckCircle2 className="mt-0.5 h-4 w-4" /> : <XCircle className="mt-0.5 h-4 w-4" />}
            <span>{test.msg}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}


export function SiteDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data: site, isLoading } = useSite(id);
  const { data: health } = useSiteHealth(id);
  const { data: cams } = useSiteCameras(id);
  const { statuses } = useStatusFeed();
  const [tab, setTab] = useState<Tab>("cameras");

  if (isLoading) return <p className="py-10 text-center text-sm">Loading…</p>;
  if (!site) {
    return (
      <p className="py-10 text-center text-sm text-destructive">
        Site not found.
      </p>
    );
  }

  const pct = site.health_pct ?? 100;

  return (
    <div className="space-y-6">
      <div>
        <Link
          to="/sites"
          className="text-xs text-muted-foreground hover:underline"
        >
          ← All sites
        </Link>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">
              {site.name}
            </h1>
            <p className="text-sm text-muted-foreground">
              <span className="font-mono">{site.code ?? "—"}</span>
              {" · "}
              {[site.city, site.state, site.country].filter(Boolean).join(", ") || "—"}
            </p>
          </div>
          <Badge variant={site.is_active ? "online" : "offline"}>
            {site.is_active ? "active" : "inactive"}
          </Badge>
        </div>
      </div>

      {/* Sprint 10 fix — NVR configuration form */}
      <NVRConfigCard site={site} />

      {/* Health summary */}
      <Card>
        <CardContent className="space-y-3 p-4">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold">Health</h2>
            <span className="text-2xl font-semibold">{Math.round(pct)}%</span>
          </div>
          <div className="h-2 w-full rounded-full bg-muted">
            <div
              className={cn("h-2 rounded-full", healthColor(pct))}
              style={{ width: `${Math.max(2, pct)}%` }}
            />
          </div>
          <div className="grid grid-cols-4 gap-2 text-center text-xs">
            <div>
              <div className="text-2xl font-semibold text-emerald-600">
                {health?.online ?? 0}
              </div>
              <div className="text-muted-foreground">online</div>
            </div>
            <div>
              <div className="text-2xl font-semibold text-zinc-500">
                {health?.offline ?? 0}
              </div>
              <div className="text-muted-foreground">offline</div>
            </div>
            <div>
              <div className="text-2xl font-semibold text-red-600">
                {health?.degraded ?? 0}
              </div>
              <div className="text-muted-foreground">errors</div>
            </div>
            <div>
              <div className="text-2xl font-semibold">
                {health?.total_cameras ?? 0}
              </div>
              <div className="text-muted-foreground">total</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <div className="flex gap-2 border-b">
        {(["cameras", "events", "settings"] as Tab[]).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={cn(
              "px-3 py-2 text-sm capitalize",
              tab === t
                ? "border-b-2 border-primary font-medium text-foreground"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === "cameras" && (
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {(cams?.items ?? []).map((c) => {
            const liveStatus = statuses[c.id] ?? c.status;
            const isOnline = liveStatus === "online";
            return (
              <Card key={c.id} className="overflow-hidden">
                <Link to={`/cameras/${c.id}`}>
                  <CameraFeedPlayer
                    hlsUrl={c.hls_url ?? undefined}
                    cameraId={c.id}
                    online={isOnline}
                    cameraName={c.name}
                    autoplay
                    controls={false}
                  />
                </Link>
                <CardContent className="flex items-center justify-between p-3 text-sm">
                  <div className="truncate">
                    <p className="truncate font-medium">{c.name}</p>
                    <p className="truncate text-xs text-muted-foreground">
                      {c.manufacturer ?? "—"}
                    </p>
                  </div>
                  <Badge
                    variant={isOnline ? "online" : "offline"}
                  >
                    {liveStatus}
                  </Badge>
                </CardContent>
              </Card>
            );
          })}
          {cams && cams.items.length === 0 && (
            <Card className="md:col-span-2 lg:col-span-3">
              <CardContent className="py-8 text-center text-sm text-muted-foreground">
                No cameras at this site yet.
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {tab === "events" && (
        <Card>
          <CardContent className="py-8 text-center text-sm text-muted-foreground">
            Per-site event timeline lands in a follow-up sprint. For now,
            see the global Audit Log.
          </CardContent>
        </Card>
      )}

      {tab === "settings" && (
        <Card>
          <CardHeader>
            <CardTitle>Site metadata</CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
              <dt className="text-muted-foreground">Name</dt>
              <dd>{site.name}</dd>
              <dt className="text-muted-foreground">Code</dt>
              <dd className="font-mono">{site.code ?? "—"}</dd>
              <dt className="text-muted-foreground">Address</dt>
              <dd>{site.address ?? "—"}</dd>
              <dt className="text-muted-foreground">Timezone</dt>
              <dd>{site.timezone}</dd>
              <dt className="text-muted-foreground">Created</dt>
              <dd>{formatDate(site.created_at)}</dd>
              <dt className="text-muted-foreground">Updated</dt>
              <dd>{formatDate(site.updated_at)}</dd>
            </dl>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
